import {
    ChatSearchResult,
    TranscriptionSearchResult,
} from "components/Evaluation/Stores/EvalStore";
import { action, computed, makeObservable, observable } from "mobx";
import { EddyEffectP2Results } from "models/EddyEffectP2Results";
import type { ConversationSegmentItem } from "services/ConversationService";
import type { ITranscriptionSegment } from "services/TranscriptionService";
import { BaseStore } from "stores/BaseStore";
import type { IRootStore } from "stores/RootStore";
import { AcxStore } from "stores/RootStore";
import { convertTranscriptToBlurbs } from "../utils";
import { Blurb } from "../Components/BlurbBox/Blurb";
import { PlayerOptions } from "components/SoundClipEditor/SoundClipEditor";
import { IChatRecord } from "components/UI/Chat/AcxChatViewer";
import { AdverseEventP2Results } from "models/AdverseEventP2Results";

const SELECTION_TIME_TOLERANCE_SECONDS = 0.001;

interface SelectedPlayingBlurb {
    id: string;
    isPlaying: boolean;
}

export interface LastTranscriptionSelection {
    selectedStartTimestamp: number | null;
    selectedEndTimestamp: number | null;
    selectingViaAudio: boolean;
    selectedText: string;
    selectedChannel: number | undefined;
    overlappingSpeakers: boolean;
    blurb: Blurb | undefined;
}

// Extend ITranscriptionSegment to allow phraseStartTime for local use
// (This does not affect the source type, just allows the property in this file)
interface ITranscriptionSegmentWithPhrase extends ITranscriptionSegment {
    phraseStartTime?: number;
}

export interface SegmentBlurbSection {
    segment: ConversationSegmentItem;
    blurbs: Blurb[];
    startTimestamp: number;
    endTimestamp: number;
    hasBlurbs: boolean;
    showHeader: boolean;
    showInJumpTo: boolean;
}

export type TranscriptionDisplayRow =
    | {
          type: "segmentHeader";
          key: string;
          section: SegmentBlurbSection;
      }
    | {
          type: "blurb";
          key: string;
          blurb: Blurb;
      }
    | {
          type: "divider";
          key: string;
      };

@AcxStore
export class AcxTranscriptionStore extends BaseStore {
    @observable searchString: string = "";
    @observable scrollIndex: number | null = null;
    @observable isCheckedEddyEffectP2: boolean = false;
    @observable eddyEffectP2Data: EddyEffectP2Results[] = [];
    @observable adverseEventP2Data: AdverseEventP2Results[] = [];
    @observable hasEvaluation: boolean = false;
    @observable selectedPlayingBlurb: SelectedPlayingBlurb = {
        id: "0",
        isPlaying: false,
    };
    @observable phrases: ITranscriptionSegment[] | undefined = undefined;
    @observable words: ITranscriptionSegment[] | undefined = undefined;
    @observable conversationSegments: ConversationSegmentItem[] = [];
    @observable focused: boolean = false;
    @observable playClip: ((options: PlayerOptions) => void) | undefined;

    @observable selectedStartTimestamp: number | null = null;
    @observable selectedEndTimestamp: number | null = null;
    @observable selectedEndTimestampTemp: number | null = null;

    @observable audioStartTimestamp: number | null = null;
    @observable audioEndTimestamp: number | null = null;
    @observable selectingViaAudio: boolean = false;

    @observable lastSelection: LastTranscriptionSelection | null = null;

    constructor(public rootStore: IRootStore) {
        super("AcxTranscriptionStore");
        makeObservable(this);
    }

    @action
    setPlayClip(playClip: ((options: PlayerOptions) => void) | undefined) {
        this.playClip = playClip;
    }

    @action
    setSearchString(searchString: string) {
        this.searchString = searchString;
    }

    @action
    setFocused(focused: boolean) {
        this.focused = focused;
    }

    @action
    setPhrases(phrases: ITranscriptionSegment[] | undefined) {
        this.phrases = phrases;
    }

    @action
    setWords(words: ITranscriptionSegment[] | undefined) {
        this.words = words;
    }

    @action
    setConversationSegments(segments: ConversationSegmentItem[] | undefined) {
        this.conversationSegments = segments ?? [];
    }

    @action
    setSelectedStartTimestamp(timestamp: number | null) {
        this.selectedStartTimestamp = timestamp;
    }

    @action
    setSelectedEndTimestamp(timestamp: number | null) {
        this.selectedEndTimestamp = timestamp;
    }

    @action
    setSelectedEndTimestampTemp(timestamp: number | null) {
        this.selectedEndTimestampTemp = timestamp;
    }

    @action
    setAudioStartTimestamp(timestamp: number | null) {
        this.audioStartTimestamp = timestamp;
    }

    @action
    setAudioEndTimestamp(timestamp: number | null) {
        this.audioEndTimestamp = timestamp;
    }

    @action
    setSelectingViaAudio(selectingViaAudio: boolean) {
        this.selectingViaAudio = selectingViaAudio;
    }

    @action
    saveLastSelection() {
        this.lastSelection = {
            selectedStartTimestamp: this.selectedStartTimestamp,
            selectedEndTimestamp: this.selectedEndTimestamp,
            selectingViaAudio: this.selectingViaAudio,
            selectedText: this.selectedText,
            selectedChannel: this.selectedBlurb?.channel,
            overlappingSpeakers: this.overlappingSpeakers,
            blurb: this.selectedBlurb,
        };
    }

    @action
    clearLastSelection() {
        this.lastSelection = null;
    }

    @action
    setEddyEffect(eddies: EddyEffectP2Results[]) {
        this.eddyEffectP2Data = eddies;
    }

    @action
    setAdverseEventData(adverseEventData: AdverseEventP2Results[]) {
        this.adverseEventP2Data = adverseEventData;
    }

    @action
    setHasEval(evaluation: boolean) {
        this.hasEvaluation = evaluation;
    }

    @action
    setScrollIndex(index: number | null) {
        this.scrollIndex = index;
    }

    @action
    getScrollIndexForTimestamp(timestamp: number): number | null {
        const rows = this.transcriptionDisplayRows;
        if (
            typeof timestamp !== "number" ||
            Number.isNaN(timestamp) ||
            !rows.length
        ) {
            return null;
        }

        // Prefer the blurb row that contains the timestamp.
        const containingIndex = rows.findIndex(
            (row) =>
                row.type === "blurb" &&
                row.blurb.timeInterval[0] <= timestamp &&
                row.blurb.timeInterval[1] >= timestamp,
        );
        if (containingIndex !== -1) {
            return containingIndex;
        }

        // Fall back to first row that starts at/after the target timestamp.
        // This handles the case where the timestamp is between blurbs
        const nextIndex = rows.findIndex(
            (row) =>
                row.type === "blurb" && row.blurb.timeInterval[0] >= timestamp,
        );
        if (nextIndex !== -1) {
            return nextIndex;
        }

        // Last blurb row if timestamp is after the final blurb.
        const lastBlurbIndex = rows.findLastIndex(
            (row) => row.type === "blurb",
        );
        return lastBlurbIndex !== -1 ? lastBlurbIndex : null;
    }

    @action
    getChatSearchResults(
        transcriptionMessages: IChatRecord[],
        strict?: boolean,
    ): ChatSearchResult[] | undefined {
        if (!transcriptionMessages || transcriptionMessages.length === 0) {
            return undefined;
        }
        if (this.searchString.length <= 2) {
            // If search string is too short, return undefined
            return undefined;
        }
        // filter messages to ones that include the search string
        const filteredMessages = transcriptionMessages.filter((message) =>
            message.text
                ?.toLowerCase()
                .includes(
                    strict
                        ? " " + this.searchString.toLowerCase() + " "
                        : this.searchString.toLowerCase(),
                ),
        );
        // filter out any messages that are empty or undefined
        const validMessages = filteredMessages.filter(
            (message) => message.text && message.text.trim() !== "",
        );
        if (validMessages.length === 0) {
            // If no valid messages, return empty array
            return [];
        }

        return validMessages.map((message) => {
            return {
                text: message.text as string, // We have filtered out undefined and empty messages
                from: message.from,
                timestamp: message.timestamp,
            };
        });
    }

    @action
    getSearchResults(params?: {
        blurbsOnly?: boolean;
        strict?: boolean;
    }): TranscriptionSearchResult[] | undefined {
        const { blurbsOnly = false, strict = false } = params || {};
        if (!this.transcriptWords) return undefined;
        if (this.searchString.length <= 2) {
            return undefined;
        }

        // BUILD RAW SEARCH RESULTS

        // If strict, use strict word boundary matching for indices
        const matchingIndices = this.getMatchingIndices(strict);
        if (!matchingIndices.length) {
            // If no matching indices, return empty array
            return [];
        }

        // Collect search statements based on matching indices
        const searchStatementResults =
            this.collectSearchStatement(matchingIndices);

        // BUILD RETURNED SEARCH RESULTS

        let searchResults: TranscriptionSearchResult[] = [];

        // For each raw search statement (collection of words)
        searchStatementResults.forEach(
            (result: ITranscriptionSegmentWithPhrase[]) => {
                let stmt = ""; // This is the final statement text to return that gets displayed in the search results
                result = result.filter((v) => v !== undefined);
                // Concatenate the text of each word in the search statement
                result.forEach((resultObj) => {
                    stmt = stmt + resultObj?.text + " ";
                });

                const statementStartTime = result[0].startTime; // Start time of first word
                const statementEndTime = result[result.length - 1].endTime; // End time of last word

                // Find the associated blurb by matching startTime within the blurb's time range
                let associatedBlurb: Blurb | undefined = undefined;
                if (
                    this.blurbs &&
                    this.blurbs.length > 0 &&
                    result.length > 0
                ) {
                    // Find a blurb that contains the start or end time of the search result
                    associatedBlurb = this.blurbs.find(
                        (blurb: Blurb) =>
                            ((blurb.timeInterval[0] <= statementStartTime &&
                                blurb.timeInterval[1] >= statementStartTime) ||
                                (blurb.timeInterval[0] <= statementEndTime &&
                                    blurb.timeInterval[1] >=
                                        statementEndTime)) &&
                            blurb.text.includes(stmt.trim()),
                    );
                }

                let resultObject: TranscriptionSearchResult = {
                    stmt: stmt,
                    startTime: statementStartTime,
                    endTime: statementEndTime,
                    phraseStartTime: statementStartTime,
                    blurb: associatedBlurb,
                    channel: result[0].channel, // Use the channel of the first word in the search statement
                };
                searchResults.push(resultObject);
            },
        );

        // Remove duplicates based on startTime, endTime, and stmt
        searchResults = searchResults.filter(
            (result, index, self) =>
                index ===
                self.findIndex(
                    (r) =>
                        r.startTime === result.startTime &&
                        r.endTime === result.endTime &&
                        r.stmt === result.stmt,
                ),
        );

        if (blurbsOnly) {
            // We want to filter out duplicate blurbs as well
            searchResults = searchResults.filter(
                (result, index, self) =>
                    index ===
                    self.findIndex(
                        (r) =>
                            r.blurb &&
                            result.blurb &&
                            r.blurb.timeInterval[0] ===
                                result.blurb.timeInterval[0] &&
                            r.blurb.timeInterval[1] ===
                                result.blurb.timeInterval[1] &&
                            r.blurb.text === result.blurb.text,
                    ),
            );
        }

        return searchResults;
    }

    private collectSearchStatement(
        matchingIndices: number[],
    ): ITranscriptionSegmentWithPhrase[][] {
        const searchStatementResults: ITranscriptionSegmentWithPhrase[][] = [];
        // For each matching transcript word we found
        matchingIndices.forEach((matchingwordIndex: number) => {
            // All words in the transcript, return if empty
            const wordArray: ITranscriptionSegmentWithPhrase[] =
                this.transcriptWords ?? [];
            if (!wordArray.length) return;

            // Get the channel of the matched word (speaker)
            const matchedChannel = wordArray[matchingwordIndex].channel;

            // Expand backwards to the start of this speaker's turn
            let startIdx = matchingwordIndex;
            while (
                startIdx > 0 &&
                wordArray[startIdx - 1].channel === matchedChannel
            ) {
                startIdx--;
            }

            // Expand forwards to the end of this speaker's turn
            let endIdx = matchingwordIndex;
            while (
                endIdx < wordArray.length - 1 &&
                wordArray[endIdx + 1].channel === matchedChannel
            ) {
                endIdx++;
            }

            // Build the search statement for this match
            let searchStatement: ITranscriptionSegmentWithPhrase[] = [];
            // Limit to a max of 15 words centered on the match if possible
            const totalWords = endIdx - startIdx + 1;
            if (totalWords > 15) {
                // Try to center the match if possible, with 7 words before and after the match
                let newStart = matchingwordIndex - 7;
                let newEnd = matchingwordIndex + 7;
                if (newStart < startIdx) {
                    // If newStart goes before the start of the speaker's turn,
                    // adjust newEnd to keep the total words at 15, and set newStart to startIdx]
                    // We couldn't get 7 before, so let newEnd extend to how many words we missed by cutting off newStart
                    newEnd += startIdx - newStart;
                    newStart = startIdx;
                }
                if (newEnd > endIdx) {
                    // If newEnd goes past the end of the speaker's turn,
                    // adjust newStart to keep the total words at 15, and set newEnd to endIdx
                    // Same reasoning as above for newStart
                    newStart -= newEnd - endIdx;
                    newEnd = endIdx;
                }
                // Ensure we don't go past the bounds of the speaker's turn
                newStart = Math.max(startIdx, newStart);
                newEnd = Math.min(endIdx, newEnd);
                // Collect the words in the search statement
                for (let i = newStart; i <= newEnd; i++) {
                    searchStatement.push(wordArray[i]);
                }
            } else {
                // If total words are less than or equal to 15, just use the full range
                for (let i = startIdx; i <= endIdx; i++) {
                    searchStatement.push(wordArray[i]);
                }
            }

            searchStatementResults.push(searchStatement);
        });

        return searchStatementResults;
    }

    private getMatchingIndices(strict: boolean): number[] {
        if (!this.transcriptWords) return [];
        let matchingIndices: number[] = [];

        // Helper to check if a string is a full word match
        function isFullWordMatch(text: string, search: string) {
            // \b doesn't work for non-word chars, so use regex with word boundaries
            // Escape regex special chars in search
            const escaped = search.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
            const regex = new RegExp(`\\b${escaped}\\b`, "i");
            return regex.test(text);
        }

        // How many words are in the search string?
        const searchTextWordCount = this.searchString.split(" ").length;

        if (searchTextWordCount > 1) {
            // If multiple words, loop through the transcript in chunks of the search text word count
            for (
                let i = 0;
                i < this.transcriptWords.length - searchTextWordCount;
                i++
            ) {
                const curPhrase = this.transcriptWords
                    .slice(i, i + searchTextWordCount)
                    .map((item) => item.text)
                    .join(" ");
                if (
                    strict
                        ? isFullWordMatch(curPhrase, this.searchString)
                        : curPhrase
                              .toLowerCase()
                              .toString()
                              .includes(this.searchString.toLowerCase())
                ) {
                    matchingIndices.push(i);
                }
            }
        } else {
            // If single word, loop through the transcript words
            this.transcriptWords.forEach(
                (transcriptionWord, transcriptionIndex) => {
                    if (
                        strict
                            ? isFullWordMatch(
                                  transcriptionWord.text,
                                  this.searchString,
                              )
                            : transcriptionWord.text
                                  .toLowerCase()
                                  .toString()
                                  .includes(this.searchString.toLowerCase())
                    ) {
                        matchingIndices.push(transcriptionIndex);
                    }
                },
            );
        }

        return matchingIndices;
    }

    @computed
    get blurbs() {
        const blurbs = convertTranscriptToBlurbs(
            this.transcriptPhrases ?? this.transcriptWords ?? [],
            this.eddyEffectP2Data,
            this.adverseEventP2Data,
            !!this.transcriptPhrases,
        );

        return blurbs.map((blurb) => ({
            ...blurb,
            text: this.stripMarksIfEvaluation(blurb.text),
        }));
    }

    @computed
    get segmentedBlurbSections(): SegmentBlurbSection[] {
        if (!this.conversationSegments.length) {
            return [];
        }

        const transcriptWords = this.transcriptWords;

        if (!transcriptWords?.length) {
            return [];
        }

        return this.conversationSegments.map((segment) => {
            const startIndex = Math.max(
                0,
                Math.min(segment.startWordIndex, transcriptWords.length),
            );
            const endIndex = Math.max(
                startIndex,
                Math.min(segment.endWordIndex, transcriptWords.length),
            );
            const segmentWords = transcriptWords.slice(startIndex, endIndex);

            if (segmentWords.length === 0) {
                return {
                    segment,
                    blurbs: [],
                    startTimestamp: 0,
                    endTimestamp: 0,
                    hasBlurbs: false,
                    showHeader: false,
                    showInJumpTo: false,
                };
            }

            const startTimestamp = segmentWords[0]?.startTime;
            const endTimestamp = segmentWords[segmentWords.length - 1]?.endTime;

            const blurbs = convertTranscriptToBlurbs(segmentWords).map(
                (blurb) => {
                    const foundEddy = this.hasSignalOverlap(
                        blurb.timeInterval,
                        this.eddyTimes,
                    );
                    const foundAdverse = this.hasSignalOverlap(
                        blurb.timeInterval,
                        this.adverseEventTimes,
                    );

                    return new Blurb(
                        this.stripMarksIfEvaluation(blurb.text),
                        blurb.channel,
                        blurb.timeInterval,
                        foundEddy,
                        foundAdverse,
                        blurb.firstWordIndex,
                        blurb.lastWordIndex,
                    );
                },
            );
            const hasBlurbs = blurbs.length > 0;
            const isExcluded = ["unable to determine"].includes(
                segment.heading.toLowerCase(),
            );
            const showHeader = hasBlurbs && !isExcluded;
            const showInJumpTo = showHeader;

            return {
                segment,
                blurbs,
                startTimestamp,
                endTimestamp,
                hasBlurbs,
                showHeader,
                showInJumpTo,
            };
        });
    }

    @computed
    get transcriptionDisplayRows(): TranscriptionDisplayRow[] {
        // Behave as we do today if there are no conversation segments
        if (!this.segmentedBlurbSections.length) {
            return this.blurbs.map((blurb, index) => ({
                type: "blurb",
                key: `blurb-${index}-${blurb.timeInterval.join("-")}`,
                blurb,
            }));
        }

        const rows: TranscriptionDisplayRow[] = [];
        const nonEmptySections = this.segmentedBlurbSections.filter(
            (section) => section.hasBlurbs,
        );

        nonEmptySections.forEach((section, sectionIndex) => {
            if (section.showHeader) {
                rows.push({
                    type: "segmentHeader",
                    key: `segment-${section.segment.id}-${sectionIndex}`,
                    section,
                });
            }

            section.blurbs.forEach((blurb, blurbIndex) => {
                rows.push({
                    type: "blurb",
                    key: `segment-${
                        section.segment.id
                    }-blurb-${blurbIndex}-${blurb.timeInterval.join("-")}`,
                    blurb,
                });
            });

            if (sectionIndex < nonEmptySections.length - 1) {
                rows.push({
                    type: "divider",
                    key: `divider-${section.segment.id}-${sectionIndex}`,
                });
            }
        });

        return rows;
    }

    @computed
    get eddyTimes() {
        if (!this.transcriptWords || !this.eddyEffectP2Data) {
            return [];
        }
        return this.eddyEffectP2Data.map((eddy) => {
            const startWord = this.transcriptWords![eddy.startWordIndex];
            const endWord = this.transcriptWords![eddy.endWordIndex - 1]; // For some reason P2 data end word is exclusive and over by 1
            const text = this.transcriptWords!.slice(
                eddy.startWordIndex,
                eddy.endWordIndex,
            )
                .map((word) => word.text)
                .join(" ");
            return {
                startTime: startWord?.startTime,
                endTime: endWord?.endTime,
                text: text,
            };
        });
    }

    @computed
    get adverseEventTimes() {
        if (!this.transcriptWords || !this.adverseEventP2Data) {
            return [];
        }
        return this.adverseEventP2Data.map((event) => {
            const startWord = this.transcriptWords![event.startWordIndex];
            const endWord = this.transcriptWords![event.endWordIndex - 1]; // For some reason P2 data end word is exclusive and over by 1
            const text = this.transcriptWords!.slice(
                event.startWordIndex,
                event.endWordIndex,
            )
                .map((word) => word.text)
                .join(" ");
            return {
                startTime: startWord?.startTime,
                endTime: endWord?.endTime,
                text: text,
            };
        });
    }

    @computed
    get transcriptWords() {
        return this.words;
    }

    @computed
    get transcriptPhrases() {
        return this.phrases;
    }

    @computed
    get speakerSegments(): {
        channel: number;
        startTime: number;
        endTime: number;
    }[] {
        if (!this.transcriptWords) return [];
        // Create an array of segments with channel, startTime, and endTime
        const segments: {
            channel: number;
            startTime: number;
            endTime: number;
        }[] = [];
        let channelStartTime: number | undefined = 0;
        let channelEndTime: number | undefined = 0;
        let currentChannel: number | undefined;
        this.transcriptWords.forEach((word) => {
            if (
                word.channel !== undefined &&
                word.startTime !== undefined &&
                word.endTime !== undefined
            ) {
                if (currentChannel !== word.channel) {
                    if (
                        currentChannel !== undefined &&
                        channelStartTime !== undefined &&
                        channelEndTime !== undefined
                    ) {
                        segments.push({
                            channel: currentChannel,
                            startTime: channelStartTime,
                            endTime: channelEndTime,
                        });
                    }
                    currentChannel = word.channel;
                    channelStartTime = word.startTime;
                }
                channelEndTime = word.endTime;
            }
        });
        return segments;
    }

    @computed
    get selectedWords() {
        if (!this.transcriptWords) {
            return [];
        }

        if (
            this.selectedStartTimestamp === null ||
            this.selectedEndTimestamp === null
        ) {
            return [];
        }

        return this.transcriptWords.filter((word) => {
            const startTime = word.startTime ?? 0;
            const endTime = word.endTime ?? 0;
            return (
                startTime >= this.selectedStartTimestamp! &&
                endTime <= this.selectedEndTimestamp!
            );
        });
    }

    @computed
    get selectedHasMultipleChannels() {
        return new Set(this.selectedWords.map((word) => word.channel)).size > 1;
    }

    @computed
    get selectedText() {
        // Build display text from the timestamp-selected words so overlapping
        // speaker selections include every word the dialog can disambiguate.
        return this.selectedWords.map((word) => word.text).join(" ");
    }

    @computed
    get selectedBlurb(): Blurb | undefined {
        if (this.selectedStartTimestamp === null || !this.blurbs?.length) {
            return undefined;
        }
        const startTime = this.selectedStartTimestamp;
        const endTime = this.selectedEndTimestamp ?? startTime;
        return this.blurbs.find(
            (blurb) =>
                blurb.timeInterval[0] <=
                    startTime + SELECTION_TIME_TOLERANCE_SECONDS &&
                blurb.timeInterval[1] >=
                    endTime - SELECTION_TIME_TOLERANCE_SECONDS,
        );
    }

    @computed
    get overlappingSpeakers() {
        if (!this.transcriptWords) {
            return false;
        }

        if (
            this.selectedStartTimestamp === null ||
            this.selectedEndTimestamp === null
        ) {
            return false;
        }

        const words = this.selectedWords;

        // Redaction can cross blurb boundaries only when the timestamp-selected
        // words include channels whose timings actually overlap.
        let overlap = false;
        for (const word of words) {
            const startTime = word.startTime ?? 0;
            const endTime = word.endTime ?? 0;
            overlap = words.some((otherWord) => {
                const otherStartTime = otherWord.startTime ?? 0;
                const otherEndTime = otherWord.endTime ?? 0;
                // If same word, they're not overlapping
                if (otherWord === word) return false;
                // If same speaker, they're not overlapping
                if (otherWord.channel === word.channel) return false;
                return otherStartTime < endTime && otherEndTime > startTime;
            });
            if (overlap) break;
        }
        return overlap;
    }

    // This function is still being used for highlighting in search results and highlighting in Chats
    formatTextWithHighlights(originalText: string) {
        let filterText = this.searchString.toLowerCase();
        let returnStatement = originalText;
        returnStatement = returnStatement
            .trim()
            .replace(
                originalText,
                `<span class="selectionColors">${originalText}</span>`,
            );

        if (
            filterText.length > 2 &&
            returnStatement.toLowerCase().includes(filterText)
        ) {
            let startIndex = 0;
            let foundIndex: number = 0;
            const foundIndices: number[] = [];
            const finalStatementArray: string[] = [];
            const returnStatementArray: string[] = returnStatement.split("");

            while (foundIndex !== -1) {
                // Collect all indices where the filter text is found
                foundIndex = returnStatement
                    .toLowerCase()
                    .indexOf(filterText, startIndex);

                foundIndices.push(foundIndex);
                startIndex = foundIndex + 1;
            }

            for (let i = 0; i < returnStatementArray.length; i++) {
                // Grab the next filter text length characters from the return statement array
                const foundText: string = returnStatementArray
                    .slice(i, i + filterText.length)
                    .join("");

                // If the current index is in the found indices, highlight it
                if (foundIndices.includes(i)) {
                    finalStatementArray.push(
                        `<span class="transcriptionHighlight selectionColors" id="highlightText">`,
                        foundText,
                        "</span>",
                    );
                    // Move the index forward by the length of the filter text
                    i += filterText.length - 1;
                } else {
                    // If not in found indices, just add the character
                    const curChar: string = returnStatementArray[i];
                    finalStatementArray.push(curChar);
                }
            }

            // Join the final statement array to form the return statement
            returnStatement = finalStatementArray.join("");
        }

        return returnStatement;
    }

    private stripMarksIfEvaluation(text: string): string {
        if (!this.hasEvaluation) return text;
        return text.replace(/<mark>(.*?)<\/mark>/g, "$1");
    }

    private hasSignalOverlap(
        blurbTimeInterval: number[],
        signalTimes: Array<{ startTime?: number; endTime?: number }>,
    ): boolean {
        return signalTimes.some((signal) => {
            if (
                typeof signal.startTime !== "number" ||
                typeof signal.endTime !== "number"
            ) {
                return false;
            }

            return (
                signal.startTime < blurbTimeInterval[1] &&
                signal.endTime > blurbTimeInterval[0]
            );
        });
    }
}
